import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";

interface User {
	id: string;
	first_name: string | null;
	last_name: string | null;
	email: string;
}

interface MentionDropdownProps {
	searchQuery: string;
	onSelect: (user: User) => void;
	onClose: () => void;
	position: { top: number; left: number };
}

export const MentionDropdown = ({ searchQuery, onSelect, onClose, position }: MentionDropdownProps) => {
	const [users, setUsers] = useState<User[]>([]);
	const [loading, setLoading] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const fetchUsers = async () => {
			setLoading(true);
			try {
				let query = supabase
					.from('profiles')
					.select('id, first_name, last_name, email')
					.eq('active', true)
					.order('first_name');

				if (searchQuery) {
					query = query.or(`first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`);
				}

				const { data, error } = await query.limit(10);

				if (!error && data)
					setUsers(data);
			} catch (error) {
				console.error('Error fetching users:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchUsers();
	}, [searchQuery]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'ArrowDown') {
				e.preventDefault();
				setSelectedIndex(prev => Math.min(prev + 1, users.length - 1));
			} else if (e.key === 'ArrowUp') {
				e.preventDefault();
				setSelectedIndex(prev => Math.max(prev - 1, 0));
			} else if (e.key === 'Enter' && users[selectedIndex]) {
				e.preventDefault();
				onSelect(users[selectedIndex]);
			} else if (e.key === 'Escape') {
				e.preventDefault();
				onClose();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [users, selectedIndex, onSelect, onClose]);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node))
				onClose();
		};

		// Use a slight delay to prevent immediate closure
		const timer = setTimeout(() => {
			document.addEventListener('mousedown', handleClickOutside);
		}, 100);

		return () => {
			clearTimeout(timer);
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [onClose]);

	const getUserDisplayName = (user: User) => {
		if (user.first_name && user.last_name)
			return `${user.first_name} ${user.last_name}`;
		if (user.first_name)
			return user.first_name;
		if (user.last_name)
			return user.last_name;
		return user.email;
	};

	if (loading)
		return (
			<Card 
				ref={dropdownRef}
				className="absolute z-[60] p-2 shadow-lg max-h-60 overflow-y-auto w-64 bg-white dark:bg-gray-800"
				style={{ top: `${position.top}px`, left: `${position.left}px` }}
			>
				<div className="text-sm text-gray-500 dark:text-gray-400 p-2">Loading...</div>
			</Card>
		);

	if (users.length === 0)
		return (
			<Card
				ref={dropdownRef}
				className="absolute z-[60] p-2 shadow-lg max-h-60 overflow-y-auto w-64 bg-white dark:bg-gray-800"
				style={{ top: `${position.top}px`, left: `${position.left}px` }}
			>
				<div className="text-sm text-gray-500 dark:text-gray-400 p-2">No users found</div>
			</Card>
		);

	return (
		<Card
			ref={dropdownRef}
			className="absolute z-[60] p-1 shadow-lg max-h-60 overflow-y-auto w-64 bg-white dark:bg-gray-800"
			style={{ top: `${position.top}px`, left: `${position.left}px` }}
		>
			{users.map((user, index) => (
				<div
					key={user.id}
					className={`px-3 py-2 cursor-pointer rounded transition-colors ${
						index === selectedIndex ? 'bg-blue-100 dark:bg-blue-900' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
					}`}
					onMouseDown={(e) => {
						e.preventDefault();
						e.stopPropagation();
						onSelect(user);
					}}
					onMouseEnter={() => setSelectedIndex(index)}
				>
					<div className="font-medium text-sm">{getUserDisplayName(user)}</div>
					<div className="text-xs text-gray-500 dark:text-gray-400">{user.email}</div>
				</div>
			))}
		</Card>
	);
};
